/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-03 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Grid, TextStyle, Tooltip } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Filter from 'src/components/containers/analytics-v2/filter';
import Chart from 'src/components/views/analytics-v2/display/chart';
import AnalyticsTable from 'src/components/views/analytics-v2/display/table';
import AnalyticsChartTable from 'src/components/views/analytics-v2/display/chart-table';

import { routes } from 'src/routes';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { formatNumberInThousand } from 'src/helpers';
import {
  buildCompareDateRange,
  buildDateRangeFromDate,
  displayDateRangeV2
} from 'src/helpers/analytics-helpers';
import {
  COLLECTION_ANALYTIC_METRIC,
  QUERY_EXECUTION_KEY,
  TOP_ITEM_IN_OVERVIEW,
  TOP_ITEM_IN_DETAIL,
  FEATURES,
  MAX_NUM_X_AXIS_POINTS_COLLECTION_CHART,
  limitXAxisPoint
} from 'src/constants/analytics';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { formatMoney } from 'src/helpers/money-format';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {};

const fromDateStateName = 'collectionAnalyticsFromDate';
const toDateStateName = 'collectionAnalyticsToDate';
const compareStateName = 'collectionAnalyticsCompare';

class CollectionAnalyticsOverview extends Component<ComposedProps, State> {
  state = {};

  componentDidMount() {
    const { fromDate, toDate, compare } = this.props;
    if (fromDate && toDate) {
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);

      this.fetchAll(fromDateUTC, toDateUTC, compare);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const { fromDate: nextFromDate, toDate: nextToDate, compare: nextCompare } = nextProps;

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      onAbortApiAnalytic();
      setTimeout(() => {
        this.fetchAll(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    // Update execution ids
    this.processUpdateExecutionId(this.props, nextProps);
  }

  processHideLandMarks = categories => {
    const landmarksTotalRevenue = document.querySelectorAll(
      '.Collection-Total-revenue .apexcharts-xaxis-tick'
    );
    landmarksTotalRevenue.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksConversionRate = document.querySelectorAll(
      '.Collection-Conversion-rate .apexcharts-xaxis-tick'
    );
    landmarksConversionRate.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksFilterClick = document.querySelectorAll(
      '.Collection-Filter-click .apexcharts-xaxis-tick'
    );
    landmarksFilterClick.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
  };

  render() {
    const {
      fromDate,
      toDate,
      compare,

      collectionTotalRevenueGenerated,
      collectionTotalRevenueGeneratedCompared,
      collectionTotalRevenueGeneratedLoading,
      collectionTotalRevenueGeneratedTotal,
      collectionTotalRevenueGeneratedPercentChange,

      collectionTotalFilterClick,
      collectionTotalFilterClickCompared,
      collectionTotalFilterClickLoading,
      collectionTotalFilterClickTotal,
      collectionTotalFilterClickPercentChange,

      collectionConversionRate,
      collectionConversionRateCompared,
      collectionConversionRateLoading,
      collectionConversionRatePercent,
      collectionConversionRatePercentChange,

      collectionTop,
      collectionTopLoading,

      collectionTopFilterOptionValue,

      collectionTopFilterOptionValueCombination,
      collectionTopFilterOptionValueCombinationLoading,

      performActionSurvey,
      openReviewAddonModal,
      moneyFormat,
      i18n
    } = this.props;
    const displayedSourceDate = displayDateRangeV2(fromDate, toDate);
    const displayedComparedDate = compare
      ? buildCompareDateRange(fromDate, toDate, buildDateRangeFromDate(fromDate, toDate), 'v2')
      : '';

    const {
      series: collectionTotalRevenueGeneratedSeries,
      categories: collectionTotalRevenueGeneratedCategories
    } = this.buildChartData(
      collectionTotalRevenueGenerated,
      collectionTotalRevenueGeneratedCompared,
      displayedSourceDate,
      displayedComparedDate,
      /* customFieldTotal = */ 'total',
      MAX_NUM_X_AXIS_POINTS_COLLECTION_CHART
    );

    const {
      series: collectionTotalFilterClickSeries,
      categories: collectionTotalFilterClickCategories
    } = this.buildChartData(
      collectionTotalFilterClick,
      collectionTotalFilterClickCompared,
      displayedSourceDate,
      displayedComparedDate,
      /* customFieldTotal = */ 'total',
      MAX_NUM_X_AXIS_POINTS_COLLECTION_CHART
    );

    const {
      series: collectionConversionRateSeries,
      categories: collectionConversionRateCategories
    } = this.buildChartData(
      collectionConversionRate,
      collectionConversionRateCompared,
      displayedSourceDate,
      displayedComparedDate,
      /* customFieldTotal = */ 'conversionRate',
      MAX_NUM_X_AXIS_POINTS_COLLECTION_CHART
    );

    const collectionTopFilterOptionValueRender = collectionTopFilterOptionValue.slice(
      0,
      TOP_ITEM_IN_OVERVIEW
    );

    const maxLengthFilterOptionValue = collectionTopFilterOptionValueRender.reduce(
      (result, item) => {
        if (_.get(item, 'filterOptionLabel.length', 0) > result)
          return item.filterOptionLabel.length;
        return result;
      },
      0
    );

    const collectionTopFilterOptionValueCombinationRender =
      collectionTopFilterOptionValueCombination.slice(0, TOP_ITEM_IN_OVERVIEW);

    const maxLengthFilterOptionValueCombination =
      collectionTopFilterOptionValueCombinationRender.reduce((result, item) => {
        const maxLengthOfEachRow = item.filterCombinations.reduce((resultPair, pair) => {
          const { filterOptionLabel } = pair;
          if (_.get(filterOptionLabel, 'length', 0) > resultPair) return filterOptionLabel.length;
          return resultPair;
        }, 0);
        if (maxLengthOfEachRow > result) return maxLengthOfEachRow;
        return result;
      }, 0);

    setTimeout(() => {
      this.processHideLandMarks(collectionTotalRevenueGeneratedCategories);
    }, 1000);

    return (
      <div className="BC-Page BC-Page__CollectionAnalytics__Overview">
        <Filter
          feature={FEATURES.COLLECTION}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={COLLECTION_ANALYTIC_METRIC.OVERVIEW}
          openReviewAddonModal={openReviewAddonModal}
        />
        <div>
          <Grid columns={{ xs: 1 }}>
            {/* Total revenue generated from collection */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Chart
                isLimitxAxis
                className="Collection-Total-revenue"
                type="line"
                suffixOfValue=""
                loading={collectionTotalRevenueGeneratedLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collectionTotalRevenueGenerated_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collection_Tooltip_TotalRevenueGenerated_cardTitle'
                )}
                unlimitedYAxis
                total={formatMoney(collectionTotalRevenueGeneratedTotal, moneyFormat)}
                percentGrowthChange={collectionTotalRevenueGeneratedPercentChange}
                series={collectionTotalRevenueGeneratedSeries}
                categories={collectionTotalRevenueGeneratedCategories}
              />
            </Grid.Cell>
            {/* Total revenue generated from collection */}

            {/* Conversion rate */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Chart
                isLimitxAxis
                className="Collection-Conversion-rate"
                type="line"
                suffixOfValue="%"
                loading={collectionConversionRateLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collectionConversionRate_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collection_Tooltip_ConversionRate_cardTitle'
                )}
                total={collectionConversionRatePercent}
                percentGrowthChange={collectionConversionRatePercentChange}
                series={collectionConversionRateSeries}
                categories={collectionConversionRateCategories}
              />
            </Grid.Cell>
            {/* Conversion rate */}

            {/* Top collections */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsTable
                smallRow
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collection_Tooltip_Top_cardTitle'
                )}
                loading={collectionTopLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collectionTop_cardTitle'
                )}
                linkDetail={routes.analytics.collectionPerformance()}
                dataTableProps={{
                  headings: [
                    i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                    i18n.translate('AdminHTML.Analytics.CollectionAnalytics.collection'),
                    i18n.translate('AdminHTML.Analytics.CollectionAnalytics.orderCount'),
                    i18n.translate('AdminHTML.Analytics.CollectionAnalytics.conversionRate'),
                    i18n.translate('AdminHTML.Analytics.CollectionAnalytics.totalRevenue')
                  ],
                  rows: this.buildFallbackDataAnalyticsTable(
                    collectionTop
                      .slice(0, TOP_ITEM_IN_OVERVIEW)
                      .map((item, index) => [
                        index + 1,
                        item.collection,
                        formatNumberInThousand(item.orderCount),
                        `${formatNumberInThousand(item.conversionRate)}%`,
                        formatNumberInThousand(item.revenue)
                      ])
                  ),
                  columnContentTypes: ['text', 'text', 'numeric', 'numeric', 'numeric']
                }}
              />
            </Grid.Cell>
            {/* Top collections */}

            {/* Total filter clicks in collection page */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartTable
                loading={collectionTotalFilterClickLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collectionTotalFilterClick'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collection_Tooltip_TotalFilterClick'
                )}
                total={collectionTotalFilterClickTotal}
                percentGrowthChange={collectionTotalFilterClickPercentChange}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="Collection-Filter-click"
                    unlimitedYAxis
                    transparent
                    type="line"
                    height="270px"
                    title={i18n.translate('AdminHTML.Analytics.Common.chart')}
                    series={collectionTotalFilterClickSeries}
                    categories={collectionTotalFilterClickCategories}
                  />
                }
                table={
                  <AnalyticsTable
                    smallRow
                    transparent
                    title={i18n.translate(
                      'AdminHTML.Analytics.CollectionAnalytics.collectionTopFilterOptionValue_cardTitle'
                    )}
                    linkDetail={routes.analytics.filterOptionValue()}
                    dataTableProps={{
                      headings: [
                        i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                        i18n.translate('AdminHTML.Analytics.CollectionAnalytics.filterOptionValue'),
                        i18n.translate('AdminHTML.Analytics.CollectionAnalytics.totalClicks')
                      ],
                      rows: this.buildFallbackDataAnalyticsTable(
                        collectionTopFilterOptionValueRender.map((item, index) => [
                          index + 1,
                          <div className="filter-option filter-option-small-table">
                            <div
                              className="filter-option-label"
                              style={{ '--width': `${maxLengthFilterOptionValue * 8}px` }}>
                              {item.filterOptionLabel}
                            </div>
                            <div className="filter-option-value">
                              <Tooltip
                                content={
                                  item.filterOptionValue.includes('class=') ? (
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: item.filterOptionValue
                                      }}></span>
                                  ) : (
                                    item.filterOptionValue
                                  )
                                }>
                                <TextStyle>
                                  {item.filterOptionValue.includes('class=') ? (
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: item.filterOptionValue
                                      }}></span>
                                  ) : (
                                    item.filterOptionValue
                                  )}
                                </TextStyle>
                              </Tooltip>
                            </div>
                          </div>,
                          formatNumberInThousand(item.totalClicks)
                        ])
                      ),
                      columnContentTypes: ['text', 'text', 'numeric']
                    }}
                  />
                }
              />
            </Grid.Cell>
            {/* Total filter clicks in collection page */}

            {/* Top filter option value combinations */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsTable
                smallRow
                title={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collectionTopFilterOptionValueCombination_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.CollectionAnalytics.collection_Tooltip_TopFilterOptionValueCombination_cardTitle'
                )}
                loading={collectionTopFilterOptionValueCombinationLoading}
                linkDetail={routes.analytics.filterOptionValueCombination()}
                dataTableProps={{
                  headings: [
                    i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                    i18n.translate('AdminHTML.Analytics.CollectionAnalytics.optionValue'),
                    i18n.translate('AdminHTML.Analytics.CollectionAnalytics.totalClicks')
                  ],
                  rows: this.buildFallbackDataAnalyticsTable(
                    collectionTopFilterOptionValueCombinationRender.map((item, index) => [
                      index + 1,
                      <div className="filter-option-combinations">
                        {item.filterCombinations.map((pair, index) => {
                          const { filterOptionLabel, filterOptionValue } = pair;
                          return (
                            <div className="filter-option" key={index}>
                              <div
                                className="filter-option-label"
                                style={{
                                  '--width': `${maxLengthFilterOptionValueCombination * 8}px`
                                }}>
                                {filterOptionLabel}
                              </div>
                              <div className="filter-option-value">
                                {filterOptionValue.includes('class=') ? (
                                  <span
                                    dangerouslySetInnerHTML={{ __html: filterOptionValue }}></span>
                                ) : (
                                  filterOptionValue
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>,
                      <div className="total-click">{formatNumberInThousand(item.totalClicks)}</div>
                    ])
                  ),
                  columnContentTypes: ['text', 'text', 'numeric']
                }}
              />
            </Grid.Cell>
            {/* Top filter option value combinations */}
          </Grid>
        </div>
      </div>
    );
  }

  buildChartData = (
    sourceDataArr,
    comparedDataArr = [],
    displayedSourceDate,
    displayedComparedDate,
    customFieldTotal, // Priority field here to get data for showing on chart,
    isLimitxAxis
  ) => {
    const { compare, timezone } = this.props;

    let categories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    // Process limit x axis max 3,4 points
    if (isLimitxAxis && isLimitxAxis < categories.length) {
      categories = limitXAxisPoint(categories, MAX_NUM_X_AXIS_POINTS_COLLECTION_CHART);
    }

    const fullCategories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    const data = sourceDataArr.map(item =>
      customFieldTotal ? item[customFieldTotal] : item.total
    );

    const series = [
      {
        name: displayedSourceDate,
        data: data,
        fullCategories
      }
    ];

    if (compare) {
      const comparedData = comparedDataArr.map(item =>
        customFieldTotal ? item[customFieldTotal] : item.total
      );
      series.push({
        name: displayedComparedDate,
        data: comparedData
      });
    }

    return {
      series,
      categories
    };
  };

  buildFallbackDataAnalyticsTable = (builtData: any[], isProductPerformance = false) => {
    if (_.isEmpty(builtData)) {
      return [...Array(5)].map((item, index) =>
        isProductPerformance ? [index + 1, '', 'N/A', 0] : [index + 1, 'N/A', 0]
      );
    }
    return builtData;
  };

  fetchAll = (fromDate, toDate, compare) => {
    const {
      collectionTotalRevenueGeneratedLoading,
      collectionConversionRateLoading,
      collectionTotalFilterClickLoading,
      collectionTopLoading,
      collectionTopFilterOptionValueLoading,
      collectionTopFilterOptionValueCombinationLoading
    } = this.props;

    if (!collectionTotalRevenueGeneratedLoading) {
      this.getCollectionTotalRevenueGenerated(fromDate, toDate, compare);
    }

    if (!collectionConversionRateLoading) {
      this.getCollectionConversionRate(fromDate, toDate, compare);
    }

    if (!collectionTotalFilterClickLoading) {
      this.getCollectionTotalFilterClick(fromDate, toDate, compare);
    }

    if (!collectionTopLoading) {
      this.getCollectionTop(fromDate, toDate, compare);
    }

    if (!collectionTopFilterOptionValueLoading) {
      this.getCollectionTopFilterOptionValue(fromDate, toDate, compare);
    }

    if (!collectionTopFilterOptionValueCombinationLoading) {
      this.getCollectionTopFilterOptionValueCombination(fromDate, toDate, compare);
    }
  };

  getCollectionTop = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.COLLECTION_TOP;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getCollectionTop(fromDate, toDate, compare, queryExecutionId, TOP_ITEM_IN_DETAIL);
  };

  getCollectionTopFilterOptionValue = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // TODO: remove this line when there's any update later
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId: 'all' });
    const queryExecutionKey = QUERY_EXECUTION_KEY.COLLECTION_TOP_FILTER_OPTION_VALUE;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getCollectionTopFilterOptionValue(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getCollectionTopFilterOptionValueCombination = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // TODO: remove this line when there's any update later
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId: 'all' });
    const queryExecutionKey = QUERY_EXECUTION_KEY.COLLECTION_TOP_FILTER_OPTION_VALUE_COMBINATION;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getCollectionTopFilterOptionValueCombination(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getCollectionTotalRevenueGenerated = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.COLLECTION_TOTAL_REVENUE_GENERATED,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.COLLECTION_TOTAL_REVENUE_GENERATED_COMPARED,
      executionIds
    );

    this.props.getCollectionTotalRevenueGenerated(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getCollectionConversionRate = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.COLLECTION_CONVERSION_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.COLLECTION_CONVERSION_RATE_COMPARED,
      executionIds
    );

    this.props.getCollectionConversionRate(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getCollectionTotalFilterClick = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.COLLECTION_TOTAL_FILTER_CLICKS,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.COLLECTION_TOTAL_FILTER_CLICKS_COMPARED,
      executionIds
    );

    this.props.getCollectionTotalFilterClick(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  processUpdateExecutionId = (props, nextProps) => {
    this.processTopCollectionExecutionId(props, nextProps);
    this.processFilterOptionExecutionId(props, nextProps);
    this.processFilterOptionCombinationExecutionId(props, nextProps);
    this.processRevenueGeneratedExecutionId(props, nextProps);
    this.processConversionRateExecutionId(props, nextProps);
    this.processCollectionTotalFilterExecutionId(props, nextProps);
  };

  processTopCollectionExecutionId = (props, nextProps) => {
    const { collectionTopQueryExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      collectionTopQueryExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOP,
        executionId: nextExecutionId
      });
    }
  };

  processFilterOptionExecutionId = (props, nextProps) => {
    const { collectionTopFilterOptionValueQueryExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      collectionTopFilterOptionValueQueryExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false,
      collectionId: 'all'
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOP_FILTER_OPTION_VALUE,
        executionId: nextExecutionId
      });
    }
  };

  processFilterOptionCombinationExecutionId = (props, nextProps) => {
    const { collectionTopFilterOptionValueCombinationQueryExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      collectionTopFilterOptionValueCombinationQueryExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false,
      collectionId: 'all'
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOP_FILTER_OPTION_VALUE_COMBINATION,
        executionId: nextExecutionId
      });
    }
  };

  processRevenueGeneratedExecutionId = (props, nextProps) => {
    const {
      collectionTotalRevenueGeneratedQueryExecutionId: executionId,
      collectionTotalRevenueGeneratedComparedQueryExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionTotalRevenueGeneratedQueryExecutionId: nextExecutionId,
      collectionTotalRevenueGeneratedComparedQueryExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOTAL_REVENUE_GENERATED,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOTAL_REVENUE_GENERATED_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  processConversionRateExecutionId = (props, nextProps) => {
    const {
      collectionConversionRateQueryExecutionId: executionId,
      collectionConversionRateComparedQueryExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionConversionRateQueryExecutionId: nextExecutionId,
      collectionConversionRateComparedQueryExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_CONVERSION_RATE,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_CONVERSION_RATE_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  processCollectionTotalFilterExecutionId = (props, nextProps) => {
    const {
      collectionTotalFilterClickQueryExecutionId: executionId,
      collectionTotalFilterClickComparedQueryExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionTotalFilterClickQueryExecutionId: nextExecutionId,
      collectionTotalFilterClickComparedQueryExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOTAL_FILTER_CLICKS,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOTAL_FILTER_CLICKS_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),

  getCollectionTotalRevenueGenerated: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getCollectionTotalRevenueGenerated(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getCollectionConversionRate: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getCollectionConversionRate(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getCollectionTotalFilterClick: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getCollectionTotalFilterClick(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getCollectionTop: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getCollectionTop(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  getCollectionTopFilterOptionValue: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getCollectionTopFilterOptionValue(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  getCollectionTopFilterOptionValueCombination: (
    fromDate,
    toDate,
    compare,
    queryExecutionId,
    top
  ) =>
    dispatch(
      AnalyticsActions.getCollectionTopFilterOptionValueCombination(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    moneyFormat: _.get(state.oauth, 'tenantApp.externalInfo.money_format', ''),
    timezone: _.get(state.oauth, 'tenantApp.externalInfo.iana_timezone', ''),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Collection total revenue generated
    collectionTotalRevenueGenerated: _.get(
      state.analyticsV2,
      'collectionTotalRevenueGenerated',
      []
    ),
    collectionTotalRevenueGeneratedCompared: _.get(
      state.analyticsV2,
      'collectionTotalRevenueGeneratedCompared',
      []
    ),
    collectionTotalRevenueGeneratedLoading: _.get(
      state.analyticsV2,
      'collectionTotalRevenueGeneratedLoading',
      false
    ),
    collectionTotalRevenueGeneratedTotal: _.get(
      state.analyticsV2,
      'collectionTotalRevenueGeneratedTotal',
      ''
    ),
    collectionTotalRevenueGeneratedPercentChange: _.get(
      state.analyticsV2,
      'collectionTotalRevenueGeneratedPercentChange',
      0
    ),
    collectionTotalRevenueGeneratedQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionTotalRevenueGeneratedQueryExecutionId',
      ''
    ),
    collectionTotalRevenueGeneratedComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionTotalRevenueGeneratedComparedQueryExecutionId'
    ),

    // Collection conversion rate
    collectionConversionRate: _.get(state.analyticsV2, 'collectionConversionRate', []),
    collectionConversionRateCompared: _.get(
      state.analyticsV2,
      'collectionConversionRateCompared',
      []
    ),
    collectionConversionRateLoading: _.get(
      state.analyticsV2,
      'collectionConversionRateLoading',
      false
    ),
    collectionConversionRatePercent: _.get(
      state.analyticsV2,
      'collectionConversionRatePercent',
      ''
    ),
    collectionConversionRatePercentChange: _.get(
      state.analyticsV2,
      'collectionConversionRatePercentChange',
      0
    ),
    collectionConversionRateQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionConversionRateQueryExecutionId',
      ''
    ),
    collectionConversionRateComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionConversionRateComparedQueryExecutionId'
    ),

    // Collection total filter clicks in collection page
    collectionTotalFilterClick: _.get(state.analyticsV2, 'collectionTotalFilterClick', []),
    collectionTotalFilterClickCompared: _.get(
      state.analyticsV2,
      'collectionTotalFilterClickCompared',
      []
    ),
    collectionTotalFilterClickLoading: _.get(
      state.analyticsV2,
      'collectionTotalFilterClickLoading',
      false
    ),
    collectionTotalFilterClickTotal: _.get(
      state.analyticsV2,
      'collectionTotalFilterClickTotal',
      ''
    ),
    collectionTotalFilterClickPercentChange: _.get(
      state.analyticsV2,
      'collectionTotalFilterClickPercentChange',
      0
    ),
    collectionTotalFilterClickQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionTotalFilterClickQueryExecutionId',
      ''
    ),
    collectionTotalFilterClickComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionTotalFilterClickComparedQueryExecutionId'
    ),

    // Collection: top
    collectionTop: _.get(state.analyticsV2, 'collectionTop', []),
    collectionTopLoading: _.get(state.analyticsV2, 'collectionTopLoading', false),
    collectionTopQueryExecutionId: _.get(state.analyticsV2, 'collectionTopQueryExecutionId'),

    // Collection: top filter option value
    collectionTopFilterOptionValue: _.get(state.analyticsV2, 'collectionTopFilterOptionValue', []),
    collectionTopFilterOptionValueLoading: _.get(
      state.analyticsV2,
      'collectionTopFilterOptionValueLoading',
      false
    ),
    collectionTopFilterOptionValueQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionTopFilterOptionValueQueryExecutionId'
    ),

    // Collection: top filter option value combination
    collectionTopFilterOptionValueCombination: _.get(
      state.analyticsV2,
      'collectionTopFilterOptionValueCombination',
      []
    ),
    collectionTopFilterOptionValueCombinationLoading: _.get(
      state.analyticsV2,
      'collectionTopFilterOptionValueCombinationLoading',
      false
    ),
    collectionTopFilterOptionValueCombinationQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionTopFilterOptionValueCombinationQueryExecutionId'
    )
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(CollectionAnalyticsOverview)
);
